import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Form,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useContext, useEffect, useRef, useState } from "react";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import InputStandard from "../../components/Inputs/InputStandard";
import { jwtDecode } from "jwt-decode";
import { UserToken } from "../../context/hooks/useAuth";
import MenuTop from "../../components/MenuTop/MenuTop";
import { SettingsContext } from "../../context/SettingsContext";
import { useToast } from "../../context/ToastContext";
import {
  SignatureCapture,
  SignatureCaptureHandle,
} from "../../components/SignatureCapture/SignatureCapture";
import exemploEmailPreview from "../../assets/img/brand/exemplo-email-config.png";
import "./Settings.css";

/** Extrai só o payload base64 de um dataURL (data:image/png;base64,XXXX). */
const dataUrlToBase64 = (dataUrl: string) => dataUrl.split(",")[1] ?? "";

const Settings = () => {
  const {
    updateMessageTitle,
    setMessageTitle,
    messageTitle,
    getMessageTitleByUserId,
    setMessageEmailBody,
    messageEmailBody,
    autoSignEnabled,
    hasStoredSignature,
    hasStoredDocument,
  } = useContext(SettingsContext);
  const showToast = useToast();
  const user = jwtDecode(localStorage.getItem("token") || "") as UserToken;

  useEffect(() => {
    const getData = async () => {
      await getMessageTitleByUserId(user.UserId);
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editPerfilState, setEditPerfilState] = useState<boolean>(true);

  // Estado local da assinatura automática, aplicado só ao salvar.
  const [autoSignDraft, setAutoSignDraft] = useState<boolean>(false);
  const [documentBase64, setDocumentBase64] = useState<string | null>(null);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [signatureTouched, setSignatureTouched] = useState<boolean>(false);

  const signatureRef = useRef<SignatureCaptureHandle>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setAutoSignDraft(autoSignEnabled);
  }, [autoSignEnabled]);

  const handleBlur = (valueAtt: any, name: string) => {
    setMessageTitle(valueAtt);
  };

  const handleBlurEmailBody = (valueAtt: any, name: string) => {
    setMessageEmailBody(valueAtt);
  };

  const resetAutoSignDraft = () => {
    setAutoSignDraft(autoSignEnabled);
    setDocumentBase64(null);
    setDocumentPreview(null);
    setSignatureTouched(false);
  };

  const handleDocumentSelected = (file?: File) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("info", "Envie uma imagem do documento (JPG ou PNG).");
      return;
    }

    setDocumentPreview(URL.createObjectURL(file));
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        setDocumentBase64(reader.result.toString().split(",")[1] ?? "");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const autoSign: {
      autoSignEnabled?: boolean | null;
      autoSignSignature?: string | null;
      autoSignDocument?: string | null;
    } = { autoSignEnabled: autoSignDraft };

    if (autoSignDraft) {
      // Só envia a assinatura quando o usuário mexeu nela ou quando ainda não há
      // nenhuma salva; caso contrário mantém a que já está no servidor.
      if (signatureTouched || !hasStoredSignature) {
        const dataUrl = signatureRef.current?.toDataURL();
        if (!dataUrl) {
          showToast(
            "info",
            "Escolha um estilo ou desenhe sua assinatura para ativar a assinatura automática."
          );
          return;
        }
        autoSign.autoSignSignature = dataUrlToBase64(dataUrl);
      } else if (!hasStoredSignature) {
        showToast(
          "info",
          "Cadastre a assinatura padrão para ativar a assinatura automática."
        );
        return;
      }

      if (documentBase64) {
        autoSign.autoSignDocument = documentBase64;
      }
    }

    const ok = await updateMessageTitle(messageTitle, messageEmailBody, autoSign);
    if (ok !== false) {
      setEditPerfilState(true);
      setSignatureTouched(false);
      setDocumentBase64(null);
      setDocumentPreview(null);
    }
  };

  return (
    <>
      <ToastContainer />
      <MenuTop />
      <LoadingSpinner />
      <Container className="mt-3" fluid>
        <Row className="d-flex justify-content-center">
          <Col className="order-xl-1" xl="12">
            <Card className="shadow" style={{ backgroundColor: "#00000000" }}>
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <div className="col-6 xs-8">
                    <span className="settings-title">
                      Configurações de E-mail para Envio de Documentos
                    </span>
                  </div>
                  <Col
                    className="text-right col-6 d-flex justify-content-end"
                    xs="4"
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "end",
                        width: "50%",
                      }}
                    >
                      {editPerfilState ? (
                        <Button
                          className="settings-btn-edit"
                          onClick={(e) => setEditPerfilState(false)}
                          size="sm"
                        >
                          Editar Configurações
                        </Button>
                      ) : (
                        <>
                          <Button
                            className="settings-btn-save"
                            style={{ flexGrow: 1 }}
                            size="sm"
                            onClick={handleSave}
                          >
                            Salvar edições
                          </Button>
                          <Button
                            className="settings-btn-cancel"
                            style={{ flexGrow: 1 }}
                            onClick={() => {
                              setEditPerfilState(true);
                              resetAutoSignDraft();
                              getMessageTitleByUserId(user.UserId);
                            }}
                            size="sm"
                          >
                            Cancelar
                          </Button>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody style={{ backgroundColor: "#ffffff" }}>
                <Form>
                  <h6 className="heading-small text-muted mb-4">
                    PERSONALIZAÇÃO DO E-MAIL
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="6" className="mb-4">
                        <InputStandard
                          handleChange={handleBlur}
                          name="name"
                          label={
                            "Assunto do E-mail (Título exibido no e-mail enviado aos signatários)"
                          }
                          value={messageTitle}
                          disabled={editPerfilState}
                        ></InputStandard>
                      </Col>
                      <Col lg="6">
                        <InputStandard
                          handleChange={handleBlurEmailBody}
                          name="emailBody"
                          label={
                            "Mensagem do Corpo do E-mail (opcional - instruções ou saudação aos signatários)"
                          }
                          value={messageEmailBody}
                          disabled={editPerfilState}
                        ></InputStandard>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />

                  <h6 className="heading-small text-muted mb-4">
                    ASSINATURA AUTOMÁTICA
                  </h6>
                  <div className="pl-lg-4">
                    <label className="settings-autosign-toggle">
                      <input
                        type="checkbox"
                        checked={autoSignDraft}
                        disabled={editPerfilState}
                        onChange={(e) => setAutoSignDraft(e.target.checked)}
                      />
                      <span>
                        Assinar automaticamente os contratos em que eu for
                        signatário
                      </span>
                    </label>
                    <p className="settings-autosign-help">
                      Com esta opção ativa, sua assinatura é aplicada assim que o
                      contrato é criado. Você não recebe e-mail nem mensagem no
                      WhatsApp para assinar, porque sua assinatura já foi
                      registrada aqui.
                    </p>

                    {autoSignDraft && (
                      <div className="settings-autosign-panel">
                        <div className="settings-autosign-field">
                          <span className="settings-autosign-label">
                            Assinatura padrão
                          </span>
                          {hasStoredSignature && !signatureTouched && (
                            <p className="settings-autosign-stored">
                              Você já tem uma assinatura cadastrada. Altere
                              abaixo apenas se quiser substituí-la.
                            </p>
                          )}
                          <SignatureCapture
                            ref={signatureRef}
                            name={user.UserName || ""}
                            disabled={editPerfilState}
                            onChange={() => setSignatureTouched(true)}
                          />
                        </div>

                        <div className="settings-autosign-field">
                          <span className="settings-autosign-label">
                            Documento de identificação (opcional)
                          </span>
                          <p className="settings-autosign-help">
                            Alguns contratos exigem a foto de um documento. Se
                            você guardar a imagem aqui, esses contratos também
                            serão assinados automaticamente. Sem ela, você
                            receberá o link para anexar o documento na hora.
                          </p>

                          {(documentPreview || hasStoredDocument) && (
                            <div className="settings-autosign-doc-preview">
                              {documentPreview ? (
                                <img
                                  src={documentPreview}
                                  alt="Pré-visualização do documento de identificação"
                                />
                              ) : (
                                <span className="settings-autosign-stored">
                                  Documento já cadastrado.
                                </span>
                              )}
                            </div>
                          )}

                          <input
                            ref={documentInputRef}
                            id="settingsAutoSignDocInput"
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={(e) =>
                              handleDocumentSelected(e.target.files?.[0])
                            }
                          />
                          <Button
                            type="button"
                            className="settings-btn-cancel"
                            size="sm"
                            disabled={editPerfilState}
                            onClick={() => documentInputRef.current?.click()}
                          >
                            {documentPreview || hasStoredDocument
                              ? "Trocar documento"
                              : "Anexar documento"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <hr className="my-4" />

                  <div className="pl-lg-4 settings-email-example">
                    <h6 className="heading-small text-muted mb-2">
                      EXEMPLO DE VISUALIZAÇÃO
                    </h6>
                    <p className="settings-email-example-caption mb-3">
                      Veja onde o assunto e a mensagem personalizada aparecem no
                      e-mail enviado aos signatários (demais trechos são fixos do
                      modelo).
                    </p>
                    <div className="settings-email-preview">
                      <img
                        src={exemploEmailPreview}
                        alt="Exemplo de e-mail: assunto no topo e mensagem no corpo, acima do aviso e do botão de visualizar documento"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Settings;
